import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { auth } from '../config/firebase';
import { checkManagerSubscriptionStatus } from '../services/authService';
import { getCompanyProfileCached } from '../services/cacheService';
import PdfViewer from '../PdfViewer';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import { logger } from '../utils/logger';
import { Language } from '../constants/strings';
import { loadSavedPdf, clearSavedPdf } from '../utils/savedPdfStorage';
import { extractExcelFromPdf } from '../services/pdfAttachmentService';
import { fetchRevisionAsFiles, fetchRevisionPdf, LocalServerError, getStorageMode } from '../services/localServerService';
import { fetchCloudRevisionAsFiles, fetchCloudSettingsPdfUrl } from '../services/cloudStorageService';
import {
    persistPdfViewerMetaFromState,
    readPdfViewerPersistedMeta,
    resetPdfViewerAndHomeFormSession,
} from '../utils/pdfViewerSessionMeta';

function getStoredReportLanguage(): Language {
    const s = localStorage.getItem('reportLanguage');
    if (s === 'et' || s === 'en' || s === 'ru') {
        return s;
    }
    return 'et';
}

type LocationState = {
    pdfFile?: File;
    detail?: string;
    order?: string;
    density?: string;
    counter?: string;
    role?: 'user' | 'editor';
    excelFile?: File | null;
    editorExcelFile?: File | null;
    engineerName?: string;
    revision?: string;
    reportLanguage?: Language;
    // Operator path: when set, the page fetches PDF + settings from the company's
    // local server instead of reading state.pdfFile / state.excelFile.
    detailNo?: string;
    revisionId?: string;
    isNewEditorRevision?: boolean;
} | null;

export default function PdfViewerPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { strings } = useLanguage();
    const [ isLoadingTools, setIsLoadingTools ] = useState(true);
    const [ hasTools, setHasTools ] = useState(false);

    const navState = location.state as LocationState;
    const [ pdfFile, setPdfFile ] = useState<File | null>(() => navState?.pdfFile ?? null);
    const [ pdfHydrated, setPdfHydrated ] = useState(() => !!navState?.pdfFile);
    const [ editorExcelFile, setEditorExcelFile ] = useState<File | null>(() => navState?.editorExcelFile ?? null);
    const [ excelFile, setExcelFile ] = useState<File | null>(() => navState?.excelFile ?? null);
    const [ revisionLabel, setRevisionLabel ] = useState<string>(() => navState?.revision ?? '');
    const [ counterFromServer, setCounterFromServer ] = useState<string | null>(null);
    const [ remoteFetchError, setRemoteFetchError ] = useState<string | null>(null);
    const [ persistedMeta, setPersistedMeta ] = useState(() => readPdfViewerPersistedMeta());

    useEffect(() => {
        if (navState?.pdfFile) {
            persistPdfViewerMetaFromState(navState as Record<string, unknown>);
            setPersistedMeta(readPdfViewerPersistedMeta());
        }
    }, [ navState ]);

    useEffect(() => {
        if (navState?.pdfFile) {
            setPdfFile(navState.pdfFile);
            setPdfHydrated(true);
            return;
        }

        // Operator/editor path: fetch by detailNo + revisionId from local server or Cloud DB.
        if (navState?.detailNo && navState?.revisionId) {
            setPdfHydrated(false);
            setRemoteFetchError(null);
            let cancelled = false;
            void (async () => {
                try {
                    const mode = await getStorageMode();
                    if (navState.isNewEditorRevision) {
                        const detailNo = navState.detailNo!;
                        const sourceRevisionId = navState.revisionId!;
                        let fetchedPdf: File;
                        if (mode === 'cloudDb') {
                            const pdfUrl = await fetchCloudSettingsPdfUrl(sourceRevisionId);
                            const pdfResponse = await fetch(pdfUrl);
                            if (!pdfResponse.ok) {
                                throw new Error(`Failed to download PDF (HTTP ${pdfResponse.status})`);
                            }
                            const pdfBlob = await pdfResponse.blob();
                            fetchedPdf = new File(
                                [ pdfBlob ],
                                `${detailNo}.pdf`,
                                { type: 'application/pdf' }
                            );
                        } else {
                            const pdfBlob = await fetchRevisionPdf(detailNo, sourceRevisionId);
                            fetchedPdf = new File(
                                [ pdfBlob ],
                                `${detailNo}.pdf`,
                                { type: 'application/pdf' }
                            );
                        }
                        if (cancelled) return;
                        setPdfFile(fetchedPdf);
                        setExcelFile(null);
                        setEditorExcelFile(null);
                        setRevisionLabel(navState.revision ?? '');
                        setCounterFromServer('');
                        setPdfHydrated(true);
                        persistPdfViewerMetaFromState({
                            detail: detailNo,
                            role: navState.role ?? 'editor',
                            revision: navState.revision ?? '',
                            order: navState.order,
                            engineerName: navState.engineerName,
                            reportLanguage: navState.reportLanguage,
                            counter: ''
                        });
                        setPersistedMeta(readPdfViewerPersistedMeta());
                        return;
                    }

                    const { pdfFile: fetchedPdf, excelFile: fetchedXlsx, revision } =
                        mode === 'cloudDb'
                            ? await fetchCloudRevisionAsFiles(navState.detailNo!, navState.revisionId!)
                            : await fetchRevisionAsFiles(navState.detailNo!, navState.revisionId!);
                    if (cancelled) return;
                    setPdfFile(fetchedPdf);
                    setExcelFile(fetchedXlsx);
                    if (navState.role === 'editor') {
                        setEditorExcelFile(fetchedXlsx);
                    }
                    setRevisionLabel(revision.revision);
                    setCounterFromServer(revision.settings.mdn ?? '');
                    setPdfHydrated(true);
                    persistPdfViewerMetaFromState({
                        detail: navState.detailNo,
                        role: navState.role ?? 'user',
                        revision: revision.revision,
                        order: navState.order,
                        engineerName: navState.engineerName,
                        reportLanguage: navState.reportLanguage,
                        counter: revision.settings.mdn ?? ''
                    });
                    setPersistedMeta(readPdfViewerPersistedMeta());
                } catch (err: unknown) {
                    if (cancelled) return;
                    const message = err instanceof LocalServerError
                        ? err.message
                        : (err instanceof Error ? err.message : 'Failed to load revision');
                    logger.error('[PdfViewerPage] Failed to fetch revision', { err });
                    setRemoteFetchError(message);
                    setPdfHydrated(true);
                }
            })();
            return () => {
                cancelled = true;
            };
        }

        setPdfHydrated(false);
        let cancelled = false;
        void (async () => {
            const mode = await getStorageMode();
            if (cancelled) return;
            if (mode !== 'manual') {
                setPdfFile(null);
                setPdfHydrated(true);
                return;
            }
            const f = await loadSavedPdf();
            if (!cancelled) {
                setPdfFile(f);
                setPdfHydrated(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [ navState?.pdfFile, navState?.detailNo, navState?.revisionId, navState?.isNewEditorRevision, location.key ]);

    const meta = (navState?.pdfFile ? navState : null) ?? persistedMeta;
    const detail =
        navState?.detailNo
        ?? meta?.detail
        ?? (typeof localStorage !== 'undefined' ? localStorage.getItem('cachedDetail') : null)
        ?? '1';
    const order = meta?.order || undefined;
    const density = meta?.density ?? '';
    const counter = counterFromServer != null
        ? counterFromServer
        : (meta?.counter != null ? String(meta.counter) : undefined);
    const role = meta?.role ?? navState?.role ?? 'editor';
    const engineerName = meta?.engineerName || undefined;
    const revision = revisionLabel || meta?.revision || '';
    const reportLanguage = (meta?.reportLanguage as Language | undefined) ?? getStoredReportLanguage();

    useEffect(() => {
        if (navState?.editorExcelFile) {
            setEditorExcelFile(navState.editorExcelFile);
            return;
        }
        if (navState?.isNewEditorRevision) {
            setEditorExcelFile(null);
            return;
        }
        // Remote revision: settings xlsx comes from server/cloud, not PDF embed.
        if (navState?.detailNo && navState?.revisionId) {
            if (role === 'editor' && excelFile) {
                setEditorExcelFile(excelFile);
            }
            return;
        }
        if (role !== 'editor' || !pdfFile) {
            setEditorExcelFile(null);
            return;
        }
        let cancelled = false;
        void extractExcelFromPdf(pdfFile).then((ex) => {
            if (!cancelled) {
                setEditorExcelFile(ex);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [ navState?.editorExcelFile, navState?.isNewEditorRevision, navState?.detailNo, navState?.revisionId, excelFile, pdfFile, role, location.key ]);

    useEffect(() => {
        const checkAccessAndTools = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) {
                    setIsLoadingTools(false);
                    setHasTools(false);
                    resetPdfViewerAndHomeFormSession();
                    void clearSavedPdf();
                    navigate('/login');
                    return;
                }

                logger.log('[PdfViewerPage] Checking subscription status...');
                const subscriptionStatus = await checkManagerSubscriptionStatus(currentUser.uid);

                if (!subscriptionStatus.hasActiveSubscription) {
                    logger.warn('[PdfViewerPage] No active subscription, redirecting to home');
                    setIsLoadingTools(false);
                    setHasTools(false);
                    resetPdfViewerAndHomeFormSession();
                    void clearSavedPdf();
                    navigate('/home');
                    return;
                }

                const companyProfile = await getCompanyProfileCached(currentUser.uid);
                if (!companyProfile) {
                    setIsLoadingTools(false);
                    setHasTools(false);
                    return;
                }

                const tools = companyProfile.tools || [];
                const totalTools = tools.length;

                setHasTools(totalTools > 0);
            } catch (error) {
                logger.error('[PdfViewerPage] Error checking access and tools:', error);
                setHasTools(false);
            } finally {
                setIsLoadingTools(false);
            }
        };

        void checkAccessAndTools();
    }, [ navigate ]);

    useEffect(() => {
        if (!pdfHydrated || pdfFile) return;
        if (remoteFetchError) return;
        resetPdfViewerAndHomeFormSession();
        void (async () => {
            const mode = await getStorageMode();
            if (mode !== 'manual') {
                await clearSavedPdf();
            }
        })();
        navigate('/home', { replace: true });
    }, [ pdfHydrated, pdfFile, remoteFetchError, navigate ]);

    const handleBack = (opts?: { preserveOperatorHomeInputs?: boolean }) => {
        resetPdfViewerAndHomeFormSession(
            opts?.preserveOperatorHomeInputs ? { preserveOperatorHomeInputs: true } : undefined
        );
        void (async () => {
            const mode = await getStorageMode();
            if (mode !== 'manual') {
                await clearSavedPdf();
            }
        })();
        navigate('/home');
    };

    const handleDensityChange = (_newDensity: string) => {
        // Handle density change if needed
    };

    if (isLoadingTools) {
        return <EngineeringLoadingScreen ariaLabel={strings.PDFVIEWER_LOADING} />;
    }

    if (!hasTools) {
        return (
            <div className="no-organization-container">
                <div className="no-organization-message">
                    {strings.NO_TOOLS_MESSAGE}
                </div>
                <button
                    className="back-button"
                    onClick={handleBack}
                >
                    {strings.BACK_BUTTON}
                </button>
            </div>
        );
    }

    if (!pdfHydrated) {
        return <EngineeringLoadingScreen ariaLabel={strings.PDFVIEWER_LOADING} />;
    }

    if (remoteFetchError) {
        return (
            <div className="no-organization-container">
                <div className="no-organization-message">
                    {remoteFetchError}
                </div>
                <button
                    className="back-button"
                    onClick={handleBack}
                >
                    {strings.BACK_BUTTON}
                </button>
            </div>
        );
    }

    const pageClassName = 'pdf-viewer-page no-legend';

    return (
        <div className={pageClassName}>
            <PdfViewer
                file={pdfFile}
                onBack={handleBack}
                detail={detail}
                order={order}
                density={density}
                counter={counter}
                role={role}
                excelFile={excelFile}
                editorExcelFile={editorExcelFile}
                engineerName={engineerName}
                revision={revision}
                revisionId={navState?.isNewEditorRevision ? undefined : navState?.revisionId}
                isNewEditorRevision={navState?.isNewEditorRevision}
                onDensityChange={handleDensityChange}
                reportLanguage={reportLanguage}
            />
        </div>
    );
}
